import "./setup-deprecation-workflow";
import "./array-shim";
import "decorator-transforms/globals";
import "./loader-shims";
import "./discourse-common-loader-shims";
import "./global-compat";
import dialogHolderCompatModules from "discourse/dialog-holder/dialog-holder-compat-modules";
import floatKitCompatModules from "discourse/float-kit/float-kit-compat-modules";
import selectKitCompatModules from "discourse/select-kit/select-kit-compat-modules";
import truthHelperCompatModules from "discourse/truth-helpers/truth-helpers-compat-modules";
defineModules("select-kit", selectKitCompatModules);
defineModules("float-kit", floatKitCompatModules);
defineModules("truth-helpers", truthHelperCompatModules);
defineModules("dialog-holder", dialogHolderCompatModules);

import { registerDiscourseImplicitInjections } from "discourse/lib/implicit-injections";

// Register Discourse's standard implicit injections on common framework classes.
registerDiscourseImplicitInjections();

import { DEBUG } from "@glimmer/env";
import Application from "@ember/application";
import { VERSION } from "@ember/version";
import { importSync } from "@embroider/macros";
import require from "require";
import { normalizeEmberEventHandling } from "discourse/lib/ember-events";
import { isRailsTesting, isTesting } from "discourse/lib/environment";
import { withPluginApi } from "discourse/lib/plugin-api";
import { buildResolver } from "discourse/resolver";

const _pluginCallbacks = [];
let _unhandledThemeErrors = [];

window.moduleBroker = {
  lookup(moduleName) {
    return require(moduleName);
  },
};

async function loadThemeFromModulePreload(link) {
  const themeId = link.dataset.themeId;
  try {
    const compatModules = (await import(/* webpackIgnore: true */ link.href))
      .default;
    for (const [key, mod] of Object.entries(compatModules)) {
      define(`discourse/theme-${themeId}/${key}`, () => mod);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to load theme ${link.dataset.themeId} from ${link.href}`,
      String(error)
    );

    if (DEBUG && (isRailsTesting() || isTesting())) {
      throw new Error(error);
    }

    fireThemeErrorEvent({ themeId: link.dataset.themeId, error });
  }
}

async function loadPluginFromModulePreload(link) {
  const pluginName = link.dataset.pluginName;
  try {
    const compatModules = (await import(/* webpackIgnore: true */ link.href))
      .default;
    for (const [key, mod] of Object.entries(compatModules)) {
      define(`discourse/plugins/${pluginName}/${key}`, () => mod);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to load plugin ${link.dataset.pluginName} from ${link.href}`,
      error
    );

    if (DEBUG) {
      if (isRailsTesting() || isTesting()) {
        throw new Error(error);
      }

      let { addError } = importSync("discourse/static/development-error");
      addError(error, link.dataset.pluginName, link.href);
    }
  }
}

export async function loadThemesAndPlugins() {
  const promises = [
    ...[
      ...document.querySelectorAll("link[rel=modulepreload][data-theme-id]"),
    ].map(loadThemeFromModulePreload),
    ...[
      ...document.querySelectorAll("link[rel=modulepreload][data-plugin-name]"),
    ].map(loadPluginFromModulePreload),
  ];

  await Promise.all(promises);
}

function defineModules(name, compatModules) {
  for (const [key, mod] of Object.entries(compatModules)) {
    define(`discourse/${name}/${key.slice(2)}`, () => mod);
  }
}

export async function loadAdmin() {
  defineModules(
    "admin",
    (
      await import(
        /* webpackChunkName: "admin" */ "discourse/admin/admin-compat-modules"
      )
    ).default
  );
}

class Discourse extends Application {
  modulePrefix = "discourse";
  rootElement = "#main";

  customEvents = {
    paste: "paste",
  };

  Resolver = buildResolver("discourse");

  // Start up the Discourse application by running all the initializers we've defined.
  start() {
    printDebugInfo();

    document.querySelectorAll("noscript").forEach((el) => el.remove());

    // Rewire event handling to eliminate event delegation for better compat
    // between Glimmer and Classic components.
    normalizeEmberEventHandling(this);

    if (Error.stackTraceLimit) {
      // We need Errors to have full stack traces for `lib/source-identifier`
      Error.stackTraceLimit = Infinity;
    }

    // Our scroll-manager service takes care of storing and restoring scroll position.
    // Disable browser handling:
    window.history.scrollRestoration = "manual";

    loadInitializers(this);
  }

  _registerPluginCode(version, code) {
    _pluginCallbacks.push({ version, code });
  }

  ready() {
    performance.mark("discourse-ready");
    document.querySelector("#d-splash")?.remove();
  }
}

function moduleThemeId(moduleName) {
  const match = moduleName.match(/^discourse\/theme\-(-?\d+)\//);
  if (match) {
    return parseInt(match[1], 10);
  }
}

function fireThemeErrorEvent({ themeId, error }) {
  const event = new CustomEvent("discourse-error", {
    cancelable: true,
    detail: { themeId, error },
  });

  const unhandled = document.dispatchEvent(event);

  if (unhandled) {
    _unhandledThemeErrors.push(event);
  }
}

export function getAndClearUnhandledThemeErrors() {
  const copy = _unhandledThemeErrors;
  _unhandledThemeErrors = [];
  return copy;
}

/**
 * Logic for loading initializers. Similar to ember-cli-load-initializers, but
 * has some discourse-specific logic to handle loading initializers from
 * plugins and themes.
 */
function loadInitializers(app) {
  let initializers = [];
  let instanceInitializers = [];

  let discourseInitializers = [];
  let discourseInstanceInitializers = [];

  for (let moduleName of Object.keys(requirejs.entries)) {
    if (moduleName.startsWith("discourse/") && !moduleName.endsWith("-test")) {
      // In discourse core, initializers follow standard Ember conventions
      if (moduleName.startsWith("discourse/initializers/")) {
        initializers.push(moduleName);
      } else if (moduleName.startsWith("discourse/instance-initializers/")) {
        instanceInitializers.push(moduleName);
      } else {
        // https://meta.discourse.org/t/updating-our-initializer-naming-patterns/241919
        //
        // For historical reasons, the naming conventions in plugins and themes
        // differs from Ember:
        //
        // | Ember                 | Discourse          |                        |
        // | initializers          | pre-initializers   | runs once per app load |
        // | instance-initializers | (api-)initializers | runs once per app boot |
        //
        // In addition, the arguments to the initialize function is different –
        // Ember initializers get either the `Application` or `ApplicationInstance`
        // as the only argument, but the "discourse style" gets an extra container
        // argument preceding that.

        const themeId = moduleThemeId(moduleName);

        if (
          themeId !== undefined ||
          moduleName.startsWith("discourse/plugins/")
        ) {
          if (moduleName.includes("/pre-initializers/")) {
            discourseInitializers.push([moduleName, themeId]);
          } else if (
            moduleName.includes("/initializers/") ||
            moduleName.includes("/api-initializers/")
          ) {
            discourseInstanceInitializers.push([moduleName, themeId]);
          }
        }
      }
    }
  }

  for (let moduleName of initializers) {
    app.initializer(resolveInitializer(moduleName));
  }

  for (let moduleName of instanceInitializers) {
    app.instanceInitializer(resolveInitializer(moduleName));
  }

  for (let [moduleName, themeId] of discourseInitializers) {
    app.initializer(resolveDiscourseInitializer(moduleName, themeId));
  }

  for (let [moduleName, themeId] of discourseInstanceInitializers) {
    app.instanceInitializer(resolveDiscourseInitializer(moduleName, themeId));
  }

  // Plugins that are registered via `<script>` tags.
  for (let [i, callback] of _pluginCallbacks.entries()) {
    app.instanceInitializer({
      name: `_discourse_plugin_${i}`,
      after: "inject-objects",
      initialize: () => withPluginApi(callback.version, callback.code),
    });
  }
}

function resolveInitializer(moduleName) {
  const module = require(moduleName, null, null, true);

  if (!module) {
    throw new Error(moduleName + " must export an initializer.");
  }

  const initializer = module["default"];

  if (!initializer) {
    throw new Error(moduleName + " must have a default export");
  }

  if (!initializer.name) {
    initializer.name = moduleName.slice(moduleName.lastIndexOf("/") + 1);
  }

  return initializer;
}

function resolveDiscourseInitializer(moduleName, themeId) {
  let initializer;

  try {
    initializer = resolveInitializer(moduleName);
  } catch (error) {
    if (!themeId || isTesting()) {
      throw error;
    } else {
      fireThemeErrorEvent({ themeId, error });
      return;
    }
  }

  const oldInitialize = initializer.initialize;

  initializer.initialize = (app) => {
    try {
      return oldInitialize.call(initializer, app.__container__, app);
    } catch (error) {
      if (!themeId || isTesting()) {
        throw error;
      } else {
        fireThemeErrorEvent({ themeId, error });
      }
    }
  };

  return initializer;
}

let printedDebugInfo = false;
function printDebugInfo() {
  if (printedDebugInfo || isTesting()) {
    return;
  }

  let str = "ℹ️ ";

  const generator = document.querySelector("meta[name=generator]")?.content;
  const parts = generator?.split(" ");
  if (parts) {
    const discourseVersion = parts[1];
    const gitVersion = parts[5]?.substr(0, 10);
    str += `Discourse v${discourseVersion} — https://github.com/discourse/discourse/commits/${gitVersion} — `;
  }

  str += `Ember v${VERSION}`;

  // eslint-disable-next-line no-console
  console.log(str);

  printedDebugInfo = true;
}

export default Discourse;
